import { find, isNumber } from "lodash";
import { useMemo } from "react";
import {
  amortizationMethodsListRealEstate,
  FormatNumber,
  isPaymentFrequencyConfigurable,
  paymentFrequencyList,
} from "utils";
import { useFormatCurrency } from "utils/customHooks/useFormatCurrency";

import { EarlyRepaymentCommentBox } from "@krea/shared/features/early-repayment";
import type { Currency } from "@krea/shared/types/common";
import { formatLenderMonthlyInterestRate } from "@krea/shared/utils/formatNumber";

import { RealEstateAmortizationMethodEnum } from "@/features/common/enums";
import { useGetCurrentLenderCompanyQuery } from "@/features/lender-companies/hooks/use-get-lender-company.query";
import type { IRealEstateBid } from "@/features/lender-responses/types/lender-response";

import type { IBidDetailsResponse } from "../../BidDetails";
import {
  BidDetailsListItem,
  BidFeesIncludedNote,
  EmptyField,
} from "../../bidHelpers";
import { earlyRepaymentLabel } from "../shared/earlyRepaymentLabel";

interface IRealEstateBidProps {
  bid: IRealEstateBid;
  currency: Currency;
}

export const RealEstateBid = ({
  bid,
  currency,
}: IRealEstateBidProps): IBidDetailsResponse => {
  const [formattedCurrencyValue] = useFormatCurrency({ currency });

  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();

  const isInterestRatePerYearActivated =
    lenderCompany?.isFeatureInterestRatePerYearActivated;

  const averageMonthlyPayment = bid.averageMonthlyPayment;
  const comparableMonthlyInterestRate = bid.comparableMonthlyInterestRate;

  const amortizationMethodsText = find(
    amortizationMethodsListRealEstate,
    (method) => method.value === bid.amortizationMethod,
  );

  // Only bullet-like methods can defer, so on every other method the row would
  // state the one value it is allowed to have.
  const paymentFrequencyText = isPaymentFrequencyConfigurable(
    bid.amortizationMethod,
  )
    ? find(
        paymentFrequencyList,
        (frequency) => frequency.value === bid.paymentFrequency,
      )
    : undefined;

  const paybackTime = bid.paybackTimeMonths;

  const details = useMemo(() => {
    const earlyRepayment = earlyRepaymentLabel(
      bid.earlyRepayment,
      formattedCurrencyValue,
    );
    return (
      <>
        <div>
          {!!amortizationMethodsText && !!amortizationMethodsText.label && (
            <BidDetailsListItem
              title="Payment plan"
              value={amortizationMethodsText.label}
            />
          )}

          {!!paymentFrequencyText && (
            <BidDetailsListItem
              title="Payment frequency"
              value={paymentFrequencyText.label}
            />
          )}

          {bid.amortizationMethod ===
            RealEstateAmortizationMethodEnum.LINEAR && (
            <>
              {isNumber(bid.amortizationDuringTerm) && (
                <BidDetailsListItem
                  title="Amortisation during loan term"
                  value={formattedCurrencyValue(bid.amortizationDuringTerm)}
                />
              )}
              {isNumber(bid.amortizationInPercent) && (
                <BidDetailsListItem
                  title="Amortisation in percent"
                  value={`${FormatNumber(bid.amortizationInPercent)}%`}
                />
              )}
            </>
          )}

          {isNumber(bid.lenderMonthlyInterest) && (
            <BidDetailsListItem
              title={`Nominal ${isInterestRatePerYearActivated ? "yearly" : "monthly"} interest rate (if specified in loan agreement)`}
              value={`${formatLenderMonthlyInterestRate(bid.lenderMonthlyInterest, "TO_YEARLY", isInterestRatePerYearActivated)}% / ${isInterestRatePerYearActivated ? "year" : "month"}`}
            />
          )}

          {!!bid.setupFee && (
            <BidDetailsListItem
              title="Setup fee"
              value={formattedCurrencyValue(bid.setupFee)}
            />
          )}

          {(!!bid.amortizationDuringTerm ||
            !!bid.averageMonthlyCost ||
            !!bid.averageMonthlyAmortisation) && (
            <>
              {!!bid.amortizationDuringTerm &&
                bid.amortizationMethod !==
                  RealEstateAmortizationMethodEnum.LINEAR && (
                  <BidDetailsListItem
                    title="Amortisation during loan term"
                    value={formattedCurrencyValue(bid.amortizationDuringTerm)}
                  />
                )}
              {!!bid.averageMonthlyCost && (
                <BidDetailsListItem
                  title="Interest payment"
                  value={formattedCurrencyValue(bid.averageMonthlyCost)}
                />
              )}
              {!!bid.averageMonthlyAmortisation && (
                <BidDetailsListItem
                  title="Principal repayment"
                  value={formattedCurrencyValue(bid.averageMonthlyAmortisation)}
                />
              )}
            </>
          )}
          {!!bid.administrationFees && (
            <BidDetailsListItem
              title="Administration fees (per month)"
              value={formattedCurrencyValue(bid.administrationFees)}
            />
          )}

          <BidDetailsListItem
            title="Cost for early repayment"
            value={earlyRepayment.label}
          />
          {earlyRepayment.comment && (
            <EarlyRepaymentCommentBox
              label="Comment"
              comment={earlyRepayment.comment}
              className="mb-4"
            />
          )}
        </div>
        <BidFeesIncludedNote />
      </>
    );
  }, [
    bid,
    formattedCurrencyValue,
    amortizationMethodsText,
    paymentFrequencyText,
    isInterestRatePerYearActivated,
  ]);

  const summary = useMemo(() => {
    return (
      <div className="flex w-full flex-col gap-2 xl:grid xl:grid-cols-4 xl:gap-4">
        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="Amount"
            value={
              bid.amount ? formattedCurrencyValue(bid.amount) : <EmptyField />
            }
          />
        </div>
        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="Loan term"
            value={
              paybackTime ? (
                `${paybackTime} ${paybackTime > 1 ? "months" : "month"}`
              ) : (
                <EmptyField />
              )
            }
          />
        </div>
        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="Monthly payment"
            value={
              isNumber(averageMonthlyPayment) ? (
                formattedCurrencyValue(averageMonthlyPayment)
              ) : (
                <EmptyField />
              )
            }
          />
        </div>

        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="Effective monthly interest"
            value={
              comparableMonthlyInterestRate ? (
                `${FormatNumber(comparableMonthlyInterestRate)}%`
              ) : (
                <EmptyField />
              )
            }
          />
        </div>
      </div>
    );
  }, [
    bid,
    formattedCurrencyValue,
    averageMonthlyPayment,
    comparableMonthlyInterestRate,
    paybackTime,
  ]);

  return {
    summary,
    details,
  };
};
